import { type ReactNode, useState, useMemo } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";
import { Button } from "../Button";
import {
  CharacterImage,
  GLOW_COLORS,
  type CharacterExpression,
} from "../CharacterImage";

export interface ChallengeWrapperProps {
  /** Character name to display */
  characterName?: string;
  /** Character image URL (legacy - will use CharacterImage component instead) */
  characterImageUrl?: string;
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Current character expression */
  expression?: CharacterExpression;
  /** Prompt text for the challenge */
  promptText?: string;
  /** Audio prompt URL */
  promptAudio?: string;
  /** Main content area (challenge-specific UI) */
  children: ReactNode;
  /** Submit button text */
  submitText?: string;
  /** Whether submit button is disabled */
  submitDisabled?: boolean;
  /** Called when submit is clicked */
  onSubmit?: () => void;
  /** Optional skip handler */
  onSkip?: () => void;
  /** Show skip button */
  showSkip?: boolean;
  /** Called when back button is clicked */
  onBack?: () => void;
  /** Whether to show celebration animation */
  showCelebration?: boolean;
  /** Whether the answer was correct (affects character expression) */
  isCorrect?: boolean;
  /** Whether waiting for answer submission */
  isSubmitted?: boolean;
  /** Additional class names */
  className?: string;
}

// Themed backgrounds for each discipline
const DISCIPLINE_BG: Record<DisciplineSlug, string> = {
  math: "from-[#1a1a2e] via-[#16213e] to-[#0f0f23]",
  spelling: "from-[#0f2f1f] via-[#1a3f2a] to-[#0a2015]",
  patterns: "from-[#1f1a2f] via-[#2a1f3f] to-[#150f25]",
  typing: "from-[#1a1a3e] via-[#16164e] to-[#0f0f33]",
  programming: "from-[#2f2015] via-[#3f2a1a] to-[#25150a]",
};

/**
 * Base wrapper component for all challenge types.
 * Provides consistent layout with character guide, prompt area,
 * content area, and action buttons.
 *
 * Features:
 * - Themed background matching discipline
 * - Animated character guide with expressions
 * - Audio instruction button
 * - Progress-aware submit button
 */
export function ChallengeWrapper({
  characterName,
  characterImageUrl,
  discipline = "math",
  expression,
  promptText,
  children,
  submitText = "Check Answer",
  submitDisabled = false,
  onSubmit,
  onSkip,
  showSkip = false,
  onBack,
  showCelebration = false,
  isCorrect,
  isSubmitted,
  className = "",
}: ChallengeWrapperProps) {
  const glowColor = GLOW_COLORS[discipline];
  const bgGradient = DISCIPLINE_BG[discipline];

  // Determine character expression based on state
  const characterExpression: CharacterExpression = useMemo(() => {
    if (expression) return expression;
    if (isSubmitted && isCorrect) return "happy";
    if (isSubmitted && !isCorrect) return "encouraging";
    return "pointing";
  }, [expression, isSubmitted, isCorrect]);

  return (
    <div
      className={`
        flex flex-col min-h-screen
        bg-gradient-to-b ${bgGradient}
        ${className}
      `}
    >
      {/* Background decorations */}
      <BackgroundDecorations discipline={discipline} />

      {/* Character Guide Header */}
      <div
        className="relative z-10 flex items-center gap-4 p-4"
        style={{
          background: `linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)`,
        }}
      >
        {/* Back Button */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className={`
              w-12 h-12 rounded-full
              flex items-center justify-center flex-shrink-0
              bg-white/10 backdrop-blur-sm border-2 border-white/20
              hover:bg-white/20 hover:border-white/30
              transition-all duration-200
              shadow-lg
            `}
            aria-label="Go back"
            style={{ boxShadow: `0 0 20px ${glowColor}30` }}
          >
            <BackArrowIcon className="w-5 h-5 text-white" />
          </button>
        )}

        {/* Character Avatar */}
        <div className="relative">
          <CharacterImage
            discipline={discipline}
            expression={characterExpression}
            size="lg"
            floating={!isSubmitted}
            glowing={isSubmitted && isCorrect}
          />

          {/* Success/encourage indicator */}
          {isSubmitted && (
            <div
              className={`
                absolute -bottom-1 -right-1 w-8 h-8 rounded-full
                flex items-center justify-center text-lg
                ${isCorrect ? "bg-meadow-500" : "bg-sunshine-500"}
                shadow-lg animate-bounce
              `}
            >
              {isCorrect ? "✓" : "💪"}
            </div>
          )}
        </div>

        {/* Character Name & Prompt */}
        <div className="flex-1 min-w-0">
          {characterName && (
            <h2
              className="font-display font-bold text-body-lg text-white"
              style={{ textShadow: `0 2px 4px ${glowColor}60` }}
            >
              {characterName}
            </h2>
          )}
          {promptText && (
            <p className="font-body text-body-xl text-white/90 leading-relaxed line-clamp-2">
              {promptText}
            </p>
          )}
        </div>

        {/* Audio Button */}
        <button
          type="button"
          className={`
            w-14 h-14 rounded-full
            flex items-center justify-center flex-shrink-0
            bg-white/10 backdrop-blur-sm border-2 border-white/20
            hover:bg-white/20 hover:border-white/30
            transition-all duration-200
            shadow-lg
          `}
          aria-label="Play audio instructions"
          style={{ boxShadow: `0 0 20px ${glowColor}30` }}
        >
          <SpeakerIcon className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6 overflow-auto">
        {/* Celebration Overlay */}
        {showCelebration && <CelebrationOverlay color={glowColor} />}

        {/* Challenge-specific content */}
        <div className="w-full max-w-2xl">{children}</div>
      </div>

      {/* Action Buttons */}
      <div
        className="relative z-10 flex gap-4 p-6"
        style={{
          background: `linear-gradient(0deg, rgba(0,0,0,0.3) 0%, transparent 100%)`,
        }}
      >
        {showSkip && onSkip && (
          <Button
            variant="ghost"
            size="md"
            onClick={onSkip}
            className="flex-shrink-0 text-white/70 hover:text-white"
          >
            Skip
          </Button>
        )}
        <Button
          variant="discipline"
          discipline={discipline}
          size="lg"
          fullWidth
          onClick={onSubmit}
          disabled={submitDisabled}
          className="shadow-xl"
          style={{
            boxShadow: `0 4px 20px ${glowColor}50`,
          }}
        >
          {submitText}
        </Button>
      </div>
    </div>
  );
}

/**
 * Background decorations for challenge screen
 */
function BackgroundDecorations({ discipline }: { discipline: DisciplineSlug }) {
  const colors = getDecorationColors(discipline);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating particles */}
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${4 + Math.random() * 8}px`,
            height: `${4 + Math.random() * 8}px`,
            background: colors[i % colors.length],
            opacity: 0.1 + Math.random() * 0.2,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${4 + Math.random() * 3}s`,
          }}
        />
      ))}
      {/* Stars */}
      {Array.from({ length: 15 }).map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute rounded-full bg-white"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
            opacity: 0.15 + Math.random() * 0.3,
            animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

function getDecorationColors(discipline: DisciplineSlug): string[] {
  const colorSets: Record<DisciplineSlug, string[]> = {
    math: ["#E8A0FF", "#F0C4FF", "#FFD93D", "#C77DDB"],
    spelling: ["#90EE90", "#B8F5C8", "#FFB8D0", "#6BCB77"],
    patterns: ["#FF6B6B", "#FFB347", "#FFE66D", "#98D89B", "#74B9FF", "#A29BFE"],
    typing: ["#4A90D9", "#7EB3E8", "#FFD700", "#3A75B0"],
    programming: ["#FF8C42", "#00D4FF", "#E8C4A0", "#B87333"],
  };
  return colorSets[discipline];
}

/**
 * Celebration overlay with sparkles
 */
function CelebrationOverlay({ color }: { color: string }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      {/* Burst of sparkles from center */}
      {Array.from({ length: 20 }).map((_, i) => {
        const angle = (i / 20) * 360;
        const distance = 100 + Math.random() * 200;
        const x = Math.cos((angle * Math.PI) / 180) * distance;
        const y = Math.sin((angle * Math.PI) / 180) * distance;

        return (
          <div
            key={i}
            className="absolute left-1/2 top-1/2"
            style={{
              animation: `celebrateBurst 1.5s ease-out forwards`,
              animationDelay: `${i * 0.03}s`,
            }}
          >
            <div
              className="w-4 h-4 rounded-full"
              style={{
                background: i % 2 === 0 ? color : "#FFD93D",
                transform: `translate(${x}px, ${y}px)`,
                boxShadow: `0 0 10px ${color}`,
              }}
            />
          </div>
        );
      })}

      {/* Falling stars */}
      {Array.from({ length: 15 }).map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            top: "-20px",
            animation: `starFall ${1 + Math.random()}s ease-in forwards`,
            animationDelay: `${Math.random() * 0.5}s`,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#FFD93D">
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
          </svg>
        </div>
      ))}

      <style>{`
        @keyframes celebrateBurst {
          0% { opacity: 1; transform: scale(0) translate(-50%, -50%); }
          60% { opacity: 1; transform: scale(1.2) translate(-50%, -50%); }
          100% { opacity: 0; transform: scale(0.8) translate(-50%, -50%); }
        }
        @keyframes starFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * Speaker icon component
 */
function SpeakerIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

/**
 * Back arrow icon component
 */
function BackArrowIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

export default ChallengeWrapper;
